/* eslint-disable @typescript-eslint/no-explicit-any */
import { MultiSelectOption } from '@dispatch/shared/types';
import {
  Autocomplete,
  AutocompleteRenderInputParams,
  Box,
  Button,
  ButtonGroup,
  Popper,
  PopperProps,
} from '@mui/material';
import React from 'react';
import {
  Controller,
  Control,
  FieldValues,
  FieldErrors,
  FieldError,
  UseFormRegister,
  useWatch,
} from 'react-hook-form';
import { AutocompleteTextField } from './AutocompleteTextField';
import { isUndefined } from 'lodash';
export interface TextFieldProps {
  id: string;
  name?: string;
  defaultValue?: string | number;
  required?: boolean;
  control: Control<FieldValues, unknown>;
  errors?: FieldErrors;
  label?: string;
  options: MultiSelectOption[];
  value?: string | number;
  register?: UseFormRegister<FieldValues>;
  error?: FieldError;
  multiple?: boolean;
  onValueChange?: (value: valueOption) => void;
  overrideOptionsLabel?: (option: MultiSelectOption) => string;
  allowSelectAll?: boolean;
  addScroll?: boolean;
  limitTags?: number;
  readOnly?: boolean;
  disabled?: boolean;
  isOptionEqualToValue?: (
    option: MultiSelectOption,
    selectValue: unknown
  ) => boolean;
  setInput: React.Dispatch<React.SetStateAction<string>>;
  loading?: boolean;
  tagLimit?: number;
}

type valueOption = MultiSelectOption | MultiSelectOption[] | null;

type SelectPopperProps = {
  allowSelectAll: boolean;
  handleSelectAll: () => void;
} & PopperProps;

const SelectPopper = function ({
  allowSelectAll,
  handleSelectAll,
  ...rest
}: SelectPopperProps) {
  return (
    <Popper {...rest}>
      {allowSelectAll ? (
        <div
          style={{ display: 'flex', width: '100%', justifyContent: 'flex-end' }}
        >
          <ButtonGroup
            color="primary"
            aria-label="outlined primary button group"
          >
            <Button
              color="primary"
              variant="text"
              onClick={() => handleSelectAll?.()}
              onMouseDown={(e) => e.preventDefault()}
            >
              Select All
            </Button>
          </ButtonGroup>
        </div>
      ) : null}
      {rest.children}
    </Popper>
  );
};

const renderOption = (
  props: React.HTMLAttributes<HTMLLIElement>,
  option: { title: string; value: string | number }
) => {
  return (
    <Box
      component="li"
      {...props}
      key={option.value}
      sx={{ maxHeight: '100px' }}
    >
      {option.title}
    </Box>
  );
};

export const AsyncSelect = ({
  id,
  name,
  defaultValue,
  required = false,
  label,
  control,
  errors,
  error,
  options,
  onValueChange,
  multiple = false,
  overrideOptionsLabel,
  allowSelectAll = false,
  addScroll = false,
  limitTags = 20,
  readOnly = false,
  disabled = false,
  isOptionEqualToValue = (option: MultiSelectOption, selectValue: unknown) => {
    const selectedValue = selectValue as MultiSelectOption;

    return option.value === selectedValue.value;
  },
  loading,
  setInput,
  tagLimit,
}: TextFieldProps) => {
  const [open, setOpen] = React.useState<boolean>(false);

  const defaultProps = {
    options: [...options],
    getOptionLabel: (option: MultiSelectOption) => option.title,
  };
  const fieldName = name || id;

  const handleMultiChange = React.useCallback(
    (onChange: (...event: unknown[]) => void, data: MultiSelectOption[]) => {
      onChange(data);
      onValueChange && onValueChange(data);
    },
    [onValueChange]
  );

  const findOption = React.useCallback(
    (option: MultiSelectOption) => {
      return options.find(({ value: findValue }) => findValue === option.value);
    },
    [options]
  );

  const getOptionLabel = React.useCallback(
    (option: MultiSelectOption) => {
      if (overrideOptionsLabel) {
        return overrideOptionsLabel(option);
      }

      if (option?.title) {
        return option.title;
      } else {
        const result = findOption(option);
        if (result) {
          return result.title;
        }
      }
      return '';
    },
    [findOption, overrideOptionsLabel]
  );

  const closePopper = () => setOpen(false);
  const openPopper = () => setOpen(true);

  const handleHeight = multiple ? '2.75rem' : '';

  const handleSelectAll = React.useCallback(
    (
      onChange: (...event: unknown[]) => void,
      selectedOptions: MultiSelectOption[]
    ) => {
      handleMultiChange(onChange, selectedOptions);
    },
    [handleMultiChange]
  );

  const renderTextField = (params: AutocompleteRenderInputParams) => {
    return (
      <AutocompleteTextField
        params={params}
        label={label}
        required={required}
        sx={{
          '.MuiAutocomplete-inputRoot': {
            minHeight: handleHeight,
            width: '100%',
          },
        }}
        error={error ? !!error : !!errors && !!errors[id]?.message}
        helperText={error ? error : !!errors && errors[id]?.message}
      />
    );
  };

  const selectPopper = React.useCallback(
    (props: PopperProps, onChange: (...event: any[]) => void) => {
      return (
        <SelectPopper
          {...props}
          handleSelectAll={() => handleSelectAll(onChange, options)}
          allowSelectAll={allowSelectAll}
        />
      );
    },
    [allowSelectAll, handleSelectAll, options]
  );

  const getOptionDisabled = (value: any) => {
    if (multiple && tagLimit) {
      return value?.length >= tagLimit;
    }

    return false;
  };

  const onInputChange = React.useCallback(
    (value: string) => {
      if (value) {
        openPopper();
      } else {
        closePopper();
      }

      setInput(value);
    },
    [setInput]
  );

  const onChangeFn = React.useCallback(
    (
      data: MultiSelectOption | MultiSelectOption[] | null,
      onChange: (...event: any[]) => void
    ) => {
      if (multiple) {
        if (!data) {
          onValueChange?.([]);
          return onChange('');
        }
        handleMultiChange(onChange, data as MultiSelectOption[]);
      } else {
        if (!data) {
          onValueChange?.(null);
          return onChange('');
        }

        const d = data as MultiSelectOption;
        onChange(data);
        onValueChange && onValueChange(d);
      }
    },
    [handleMultiChange, multiple, onValueChange]
  );

  const currentValue = useWatch({ control, name: String(name) });

  const handleInputChange = React.useCallback(
    (_, value) => {
      onInputChange(value);
      if (!isUndefined(currentValue?.value)) {
        closePopper();
      }
    },
    [currentValue]
  );

  // controller is built in to react-hook-form and it allows your input to be registered with useForms internal state.
  return (
    <Controller
      data-testid="select-component"
      control={control}
      name={fieldName}
      defaultValue={defaultValue}
      render={({ field: { onChange, value } }) => {
        return (
          <>
            <Autocomplete
              {...defaultProps}
              loading={loading}
              readOnly={readOnly}
              disabled={disabled}
              open={open}
              disablePortal={true}
              getOptionDisabled={() => getOptionDisabled(value)}
              filterOptions={(x) => x}
              clearOnBlur={!multiple}
              onOpen={openPopper}
              onClose={closePopper}
              filterSelectedOptions
              limitTags={limitTags}
              ListboxProps={{ style: { maxHeight: '15rem' } }}
              componentsProps={{
                paper: {
                  sx: (theme) => ({
                    backgroundColor: theme.palette.background.paper,
                    // Cancel MUI's dark elevation overlay so it matches the
                    // themed popover surface instead of rendering light.
                    backgroundImage: 'none',
                    color: theme.palette.text.primary,
                  }),
                },
              }}
              multiple={multiple}
              getOptionLabel={getOptionLabel}
              value={value || (multiple ? [] : null)}
              renderInput={(params) => renderTextField(params)}
              PopperComponent={(props: PopperProps) =>
                selectPopper(props, onChange)
              }
              isOptionEqualToValue={isOptionEqualToValue}
              onInputChange={handleInputChange}
              onChange={(
                _,
                data: MultiSelectOption | MultiSelectOption[] | null
              ) => {
                onChangeFn(data, onChange);
              }}
              renderOption={(
                props: React.HTMLAttributes<HTMLLIElement>,
                option: { title: string; value: string | number }
              ) => renderOption(props, option)}
            />
            {value?.length > 20 && open && addScroll ? (
              <div style={{ height: '31.25rem' }}></div>
            ) : null}
          </>
        );
      }}
    />
  );
};
